import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import Masonry from '@mui/lab/Masonry';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import Radio from '@mui/material/Radio';
import { useParams, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UseToast from '../../UseToast';

function QuestionBank() {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['']);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [questions, setQuestions] = useState([]);
  const [questionPoint, setQuestionPoint] = useState(1);
  const [editOpen, setEditOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [brandColor, setBrandColor] = useState(null);
  const navigate = useNavigate();
  const { showMsgSuccessToast, showErrorToast } = UseToast(); // Use the custom hook
  const [searchText, setSearchText] = useState('');
  const [filteredQuestions, setFilteredQuestions] = useState(questions);
  const [randomPickedQuestion, setRandomPickedQuestion] = useState(0);

  // Language support
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  const { userId, topicId } = useParams(); // Get the optional client ID from the route

  // Function to change language
  const handleLanguageChange = async (langCode) => {
    setCurrentLanguage(langCode);
    
    // Save preference to localStorage
    localStorage.setItem(`trivia_language_pref_${userId}`, langCode);
    
    // Fetch data with new language
    await fetchAll(langCode);
    
    // Show success message
    showMsgSuccessToast(`Language changed to ${getLanguageName(langCode)}`);
  };

  // Add Question
  const handleSubmit = async e => {
    e.preventDefault();

    if (!correctAnswer) {
      showErrorToast('Please one correct answer');
      return;
    }

    if (currentId) {
      await axios.post(`/api/questions/update/${currentId}`, {
        question,
        options,
        correctAnswer,
        userId,
        topicId,
        questionPoint,
        currentLanguage, // Add current language for auto-translation
      });
      showMsgSuccessToast(`Question Updated and Auto-Translated to ${availableLanguages.length} languages`);
    } else {
      await axios.post('/api/questions/add', {
        question,
        options,
        correctAnswer,
        userId,
        topicId: topicId,
        questionPoint,
        currentLanguage, // Add current language for auto-translation
      });
      showMsgSuccessToast(`Question Added and Auto-Translated to ${availableLanguages.length} languages`);
    }
    fetchAll();
    //window.location.reload();
  };

  // Fetch Questions
  useEffect(() => {
    if (userId && topicId) {
      // Load available languages first, then load data
      fetchAvailableLanguages();
    }
  }, [userId, topicId]);

  const fetchAvailableLanguages = async () => {
    setLoadingLanguages(true);
    try {
      const response = await axios.get(`/api/language/${userId}`);
      if (response.data.success) {
        setAvailableLanguages(response.data.languages);
        
        // Check for saved language preference
        const savedLang = localStorage.getItem(`trivia_language_pref_${userId}`);
        if (savedLang && response.data.languages.includes(savedLang)) {
          setCurrentLanguage(savedLang);
          // Load data with saved language
          fetchAll(savedLang);
        } else {
          // Default to first language or 'en'
          setCurrentLanguage(response.data.languages[0] || 'en');
          fetchAll(response.data.languages[0] || 'en');
        }
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setAvailableLanguages(['en']);
      setCurrentLanguage('en');
      fetchAll('en');
    } finally {
      setLoadingLanguages(false);
    }
  };

  const fetchAll = (lang = currentLanguage) => {
    axios.get(`/api/topic-settings/${userId}/${topicId}/${lang}`).then(res => {
      let response = res.data?.questions;
      let randomPickedQuestion = res.data?.randomPickedQuestion?.randomPickedQuestion ?? 0;
      console.log('randomPickedQuestion', randomPickedQuestion);
      setRandomPickedQuestion(randomPickedQuestion);
      setQuestions(response);
      setFilteredQuestions(response);
      setBrandColor(res.data?.site_brand_color);
    });
  };

  // Delete Question
  const handleDelete = async id => {
    await axios.delete(`/api/questions/delete/${id}`);
    alert('Question Deleted');
    window.location.reload();
  };

  // Handle Adding New Option
  const handleAddOption = () => {
    setOptions([...options, '']);
  };

  // Handle Removing Option
  const handleRemoveOption = index => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
    if (correctAnswer === options[index]) {
      setCorrectAnswer('');
    }
  };

  // Handle Selecting Correct Answer
  const handleCorrectAnswer = index => {
    setCorrectAnswer(options[index]);
  };

  // Handle Open Edit Modal
  const handleEdit = question => {
    setCurrentId(question._id);
    setQuestion(question.question);
    setOptions(question.options);
    setCorrectAnswer(question.correctAnswer);
    setQuestionPoint(question.questionPoint);
    setEditOpen(true);
  };

  const columns = [
    {
      name: 'Question Name',
      selector: row => row.question,
      sortable: true,
    },
    {
      name: 'Question Options',
      selector: row => row.options.join(', '),
    },
    {
      name: 'Question Point',
      selector: row => row.questionPoint,
    },
    {
      name: 'Correct Answer',
      selector: row => row.correctAnswer,
    },

    {
      name: 'Actions',
      cell: row => (
        <>
          <IconButton onClick={() => handleEdit(row)} style={{ cursor: 'pointer' }}>
            <EditIcon style={{ color: brandColor }} />
          </IconButton>

          <IconButton onClick={() => handleDelete(row._id)} style={{ cursor: 'pointer' }}>
            <DeleteIcon style={{ color: brandColor }} />
          </IconButton>
        </>
      ),
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontWeight: 'bold', // Bold header text
        fontSize: '16px', // Increase font size
        backgroundColor: '#f4f4f4', // Light gray background
      },
    },
  };

  // Handle Search Functionality
  const handleSearch = e => {
    setSearchText(e.target.value);
    const filteredData = questions.filter(question =>
      question.question.toLowerCase().includes(e.target.value.toLowerCase())
    );
    setFilteredQuestions(filteredData);
  };

  const updateTopicSettings = async () => {
    await axios
      .post(`/api/update-topic-settings`, {
        topicId,
        randomPickedQuestion,
      })
      .then(res => {
        let response = res.data;

        console.log('response', response);
      });
  };

  return (
    <div className="main-container">
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className="page-header">
            <div className="row">
              {/* <div className="col-md-6 col-sm-12">
                        <div className="title">
                            <h4>Main Menu</h4>
                        </div>
                    </div> */}

              <div className="col-md-6 col-sm-12">
                <div className="title">
                  <h4>Questions Pool</h4>
                </div>
              </div>

              <div className="col-md-6 col-sm-12" style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '15px' }}>
                  {/* Language Selector */}
                  {!loadingLanguages && availableLanguages.length > 1 && (
                    <div className="dropdown">
                      <button
                        className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{
                          borderColor: brandColor,
                          color: brandColor
                        }}
                      >
                        <span 
                          className={`fi fi-${getLanguageFlag(currentLanguage)} me-2`}
                          style={{ fontSize: '16px' }}
                        ></span>
                        <span className="me-1">{getLanguageName(currentLanguage)}</span>
                        <span className="badge bg-primary ms-1" style={{ backgroundColor: brandColor }}>
                          {availableLanguages.length}
                        </span>
                      </button>
                      <ul className="dropdown-menu">
                        {availableLanguages.map(lang => (
                          <li key={lang}>
                            <button
                              className={`dropdown-item d-flex align-items-center ${currentLanguage === lang ? 'active' : ''}`}
                              type="button"
                              onClick={() => handleLanguageChange(lang)}
                              style={{
                                backgroundColor: currentLanguage === lang ? `${brandColor}20` : 'transparent'
                              }}
                            >
                              <span 
                                className={`fi fi-${getLanguageFlag(lang)} me-2`}
                                style={{ fontSize: '16px' }}
                              ></span>
                              <span>{getLanguageName(lang)}</span>
                              {currentLanguage === lang && (
                                <i className="fas fa-check ms-auto text-success"></i>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!loadingLanguages && availableLanguages.length === 1 && (
                    <div className="d-flex align-items-center text-muted">
                      <span 
                        className={`fi fi-${getLanguageFlag(currentLanguage)} me-2`}
                        style={{ fontSize: '16px' }}
                      ></span>
                      <span>{getLanguageName(currentLanguage)}</span>
                      <small className="ms-2">(Only language available)</small>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{ background: brandColor }}
                    className="btn btn-success"
                  >
                    {`<< Go Back`}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="dashboard-container-question">
            <Container
              maxWidth="md"
              style={{
                marginTop: '20px',
                background: '#fff',
                border: '1px solid #ddd',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                marginBottom: '20px',
                padding: '38px',
              }}
            >
              <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>
                📚 Add Question
              </Typography>

              <form onSubmit={handleSubmit} style={{ marginBottom: '20px' }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Enter Question"
                  value={question}
                  onChange={e => setQuestion(e.target.value)}
                  margin="normal"
                />
                {options.map((option, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={`Option ${index + 1}`}
                      value={option}
                      onChange={e => {
                        const newOptions = [...options];
                        newOptions[index] = e.target.value;
                        setOptions(newOptions);
                      }}
                      margin="normal"
                    />
                    <Radio
                      checked={correctAnswer === option}
                      onChange={() => handleCorrectAnswer(index)}
                    />
                    <IconButton onClick={() => handleRemoveOption(index)} color="error">
                      <RemoveCircleIcon />
                    </IconButton>
                  </div>
                ))}

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Enter Question Point"
                  value={questionPoint}
                  onChange={e => setQuestionPoint(e.target.value)}
                  margin="normal"
                  type="number"
                />

                <Button
                  startIcon={<AddCircleIcon />}
                  onClick={handleAddOption}
                  style={{ color: brandColor }}
                >
                  Add Option
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  style={{ marginTop: '10px', background: brandColor }}
                >
                  {currentId ? 'Update Question' : 'Add Question'}
                </Button>
              </form>

              {/* <Masonry columns={3} spacing={2}>
                    {questions.map(q => (
                        <Card key={q._id}>
                            <CardContent>
                                <Typography variant="h6">{q.question}</Typography>
                                <Typography variant="body2">Options: {q.options.join(', ')}</Typography>
                                <Typography variant="body2"><b>Answer:</b> {q.correctAnswer}</Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => handleEdit(q)}
                                    style={{ marginRight: '10px' }}
                                >
                                    Edit
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => handleDelete(q._id)}
                                >
                                    Delete
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </Masonry> */}

              {/* <Dialog open={editOpen} onClose={handleClose}>
                    <DialogTitle>Edit Question</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Edit Question"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            margin="normal"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} color="secondary">Cancel</Button>
                        <Button onClick={handleSubmit} color="primary">Update</Button>
                    </DialogActions>
                </Dialog> */}
            </Container>
          </div>

          <Container maxWidth="xl" style={{ marginTop: '20px' }}>
            <Typography
              variant="h5"
              gutterBottom
              style={{ textAlign: 'center', marginTop: '20px' }}
            >
              📝 All Questions
            </Typography>

            <DataTable
              columns={columns}
              data={filteredQuestions}
              customStyles={customStyles}
              pagination
              highlightOnHover
              subHeader
              subHeaderComponent={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '10px', // Adds spacing between text fields
                    flexWrap: 'wrap', // Prevents layout breaking on smaller screens
                    width: '100%',
                  }}
                >
                  {/* Left TextField (Number Input) */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '24px',
                    }}
                  >
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Enter Random Picked Questions No."
                      type="number"
                      value={randomPickedQuestion}
                      onChange={e => {
                        let value = Number(e.target.value);
                        if (filteredQuestions.length > 0) {
                          value = Math.max(1, Math.min(value, filteredQuestions.length));
                        } else {
                          value = 1; // Prevent issues if `filteredQuestions` is empty
                        }
                        setRandomPickedQuestion(value);
                      }}
                      inputMode="numeric" // Ensures numeric keyboard on mobile
                      style={{ width: '250px' }}
                    />

                    <Button
                      variant="contained"
                      onClick={() => updateTopicSettings()}
                      style={{ backgroundColor: brandColor, height: '38px' }}
                    >
                      Update
                    </Button>
                  </div>

                  {/* Right TextField (Search Input) */}
                  <TextField
                    variant="outlined"
                    size="small"
                    label="Search Question"
                    value={searchText}
                    onChange={handleSearch}
                    style={{ width: '250px' }}
                  />
                </div>
              }
              subHeaderAlign="right"
            />
          </Container>
        </div>
      </div>
    </div>
  );
}

// Helper function to get language name
const getLanguageName = (code) => {
  const languageNames = {
    'en': 'English',
    'es': 'Spanish',
    'fr': 'French',
    'de': 'German',
    'it': 'Italian',
    'pt': 'Portuguese',
    'ru': 'Russian',
    'zh': 'Chinese',
    'ja': 'Japanese',
    'ko': 'Korean',
    'ar': 'Arabic',
    'hi': 'Hindi',
    'tr': 'Turkish',
    'nl': 'Dutch',
    'sv': 'Swedish',
    'pl': 'Polish',
    'th': 'Thai',
    'vi': 'Vietnamese',
    'id': 'Indonesian'
  };
  return languageNames[code] || code.toUpperCase();
};

// Helper function to get language flag code
const getLanguageFlag = (code) => {
  const flagMap = {
    'en': 'us',
    'es': 'es',
    'fr': 'fr',
    'de': 'de',
    'it': 'it',
    'pt': 'pt',
    'ru': 'ru',
    'zh': 'cn',
    'ja': 'jp',
    'ko': 'kr',
    'ar': 'sa',
    'hi': 'in',
    'tr': 'tr',
    'nl': 'nl',
    'sv': 'se',
    'pl': 'pl',
    'th': 'th',
    'vi': 'vn',
    'id': 'id'
  };
  return flagMap[code] || 'xx'; // 'xx' is a generic flag
};

export default QuestionBank;
